import { useCallback, useMemo, useState } from 'react';

import { VideoPlayer, VideoPlayerOptions } from 'components/VideoPlayer';
import { VideoThumbnail } from 'components/VideoThumbnail';
import { VideoThumbnailSource } from 'components/VideoThumbnail/VideoThumbnailSource';
import { VideoSize } from 'types/Video/VideoSize';
import { getVideoSourceTypeFromUrl } from 'utils/video/getVideoSourceTypeFromUrl';

type Props = {
  videoUrl: string;
  thumbnailUrl?: string;
  size: VideoSize;
  largePlayButton?: boolean;
  thumbnailQaId?: string;
  playerQaId?: string;
  playerOptions?: VideoPlayerOptions;
};

export function VideoThumbnailPlayer({
  videoUrl,
  thumbnailUrl,
  size,
  largePlayButton,
  thumbnailQaId,
  playerQaId,
  playerOptions,
}: Props) {
  const [state, setState] = useState<'thumbnail' | 'player'>('thumbnail');

  const showPlayer = useCallback(() => {
    setState('player');
  }, []);

  const videoThumbnailSource: VideoThumbnailSource | undefined = useMemo(() => {
    const sourceType = getVideoSourceTypeFromUrl(videoUrl);
    if (thumbnailUrl) return { type: 'image-url', imageUrl: thumbnailUrl };

    // Raw videos don't have thumbnails
    return sourceType === 'raw'
      ? undefined
      : { type: 'video-thumbnail', videoUrl };
  }, [thumbnailUrl, videoUrl]);

  // When there is no thumbnail, display the video directly without autoplaying
  const shouldAutoplay = Boolean(videoThumbnailSource);

  if (state === 'thumbnail' && videoThumbnailSource) {
    return (
      <VideoThumbnail
        src={videoThumbnailSource}
        size={size}
        playButton={largePlayButton ? 'large' : 'small'}
        onClick={showPlayer}
        qaId={thumbnailQaId}
      />
    );
  }

  return (
    <VideoPlayer
      url={videoUrl}
      size={size}
      options={playerOptions}
      qaId={playerQaId}
      autoPlay={shouldAutoplay}
    />
  );
}
